"use strict";
/**
 * Kit Integration for Wallet Adapters
 *
 * Creates Kit-compatible signers from wallet adapters.
 * This module bridges the gap between legacy wallet adapters and modern Kit architecture.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createKitSignersFromAdapter = createKitSignersFromAdapter;
exports.adapterSupportsMessageSigning = adapterSupportsMessageSigning;
exports.adapterSupportsTransactionSending = adapterSupportsTransactionSending;
var errors_1 = require("@hermis/errors");
var solana_headless_core_1 = require("@hermis/solana-headless-core");
var chain_utils_1 = require("./chain-utils");
/**
 * Create Kit-compatible signers from a wallet adapter
 *
 * This function bridges legacy wallet adapters with modern Kit architecture.
 * It's framework-agnostic and can be used in any JavaScript environment.
 *
 * The network is automatically detected from the connection's RPC endpoint.
 * For custom RPC URLs, you can override detection with the optional `network` parameter.
 *
 * **DualConnection Support:**
 * This function accepts both legacy `Connection` from @solana/web3.js and Kit `Rpc`.
 * Wallet adapters created with the Hermis wallet standard support both connection types
 * through their `sendTransaction` and `signAndSendTransaction` methods.
 *
 * @param adapter - The wallet adapter instance (must be connected)
 * @param connection - The Solana connection (supports both web3.js Connection and Kit Rpc)
 * @param network - Optional network override ('mainnet' | 'devnet' | 'testnet')
 * @returns Kit signers object with address and signer instances
 *
 * @example
 * ```typescript
 * import { Connection } from "@solana/web3.js";
 * import { createKitSignersFromAdapter } from '@hermis/solana-headless-adapter-base';
 *
 * const connection = new Connection('https://api.devnet.solana.com');
 * const {address, messageSigner, transactionSigner} = createKitSignersFromAdapter(
 *   walletAdapter,
 *   connection
 * );
 * ```
 */
function createKitSignersFromAdapter(adapter, connection, network) {
    var _this = this;
    // If no adapter or not connected, return null values
    if (!adapter || !adapter.publicKey) {
        return {
            address: null,
            addressString: null,
            messageSigner: null,
            transactionSigner: null,
        };
    }
    // Detect network from connection (or use provided override)
    var detectedNetwork = network || (0, chain_utils_1.getNetworkFromConnection)(connection);
    var chain = (0, chain_utils_1.getChainId)(detectedNetwork);
    // Convert publicKey to Kit Address
    var walletAddress = (0, solana_headless_core_1.publicKeyToAddress)(adapter.publicKey);
    var walletAddressString = (0, solana_headless_core_1.publicKeyToString)(adapter.publicKey);
    if (!walletAddress) {
        return {
            address: null,
            addressString: null,
            messageSigner: null,
            transactionSigner: null,
        };
    }
    // Create message signer if adapter supports message signing
    var messageSigner = 'signMessage' in adapter && typeof adapter.signMessage === 'function'
        ? (0, solana_headless_core_1.createMessageSignerFromWallet)(walletAddress, function (message) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!('signMessage' in adapter) || typeof adapter.signMessage !== 'function') {
                            throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, { featureName: 'signMessage', walletName: adapter.name || 'Unknown wallet' });
                        }
                        return [4 /*yield*/, adapter.signMessage(message)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        }); })
        : null;
    // Create transaction sending signer if adapter supports sending transactions
    // Prefer signAndSendTransaction over sendTransaction as it's more efficient
    var transactionSigner = ('signAndSendTransaction' in adapter || 'sendTransaction' in adapter)
        ? (0, solana_headless_core_1.createTransactionSendingSignerFromWallet)(walletAddress, chain, function (transaction) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!('signAndSendTransaction' in adapter && typeof adapter.signAndSendTransaction === 'function')) return [3 /*break*/, 2];
                        return [4 /*yield*/, adapter.signAndSendTransaction(transaction, connection)];
                    case 1: return [2 /*return*/, _a.sent()];
                    case 2:
                        if (!('sendTransaction' in adapter && typeof adapter.sendTransaction === 'function')) return [3 /*break*/, 4];
                        return [4 /*yield*/, adapter.sendTransaction(transaction, connection)];
                    case 3: return [2 /*return*/, _a.sent()];
                    case 4: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, { featureName: 'sendTransaction or signAndSendTransaction', walletName: adapter.name || 'Unknown wallet' });
                }
            });
        }); })
        : null;
    return {
        address: walletAddress,
        addressString: walletAddressString,
        messageSigner: messageSigner,
        transactionSigner: transactionSigner,
    };
}
/**
 * Check if an adapter supports message signing
 *
 * @param adapter - The wallet adapter to check
 * @returns true if the adapter supports message signing, false otherwise
 */
function adapterSupportsMessageSigning(adapter) {
    return !!(adapter && 'signMessage' in adapter && typeof adapter.signMessage === 'function');
}
/**
 * Check if an adapter supports transaction sending
 *
 * @param adapter - The wallet adapter to check
 * @returns true if the adapter supports transaction sending, false otherwise
 */
function adapterSupportsTransactionSending(adapter) {
    return !!(adapter && 'sendTransaction' in adapter && typeof adapter.sendTransaction === 'function');
}
